// The HUD's top-right column in the "E · Oro suave" style: inside a tile, the time controls (PRD
// section 19.13) with a soft sign while paused; on both levels, the 3D/Cenital switch (19.14) and
// below it, like the design system's side menu, the button that opens the performance meter.
import type { CameraMode } from '../render/camera-rig';
import { frameButton, segmentedPill, type Control } from './controls';
import { h } from './dom';
import { ICONS } from './icons';

/** Pause or a speed of the game clock. */
export type TimeChoice = 'pause' | '1' | '2' | '4';

export interface HudHandlers {
  readonly onCamera: (mode: CameraMode) => void;
  readonly onMeterToggle: () => void;
  readonly onTime: (choice: TimeChoice) => void;
}

export class Hud {
  readonly el: HTMLElement;
  readonly camera: Control<CameraMode>;
  /** Pressed while the performance meter is open. */
  readonly meter: Control<boolean>;
  readonly time: Control<TimeChoice>;
  private readonly paused = h('span', { class: 'paused-chip', hidden: true }, 'En pausa · puedes seguir construyendo');

  constructor(mode: CameraMode, meterOpen: boolean, handlers: HudHandlers) {
    this.time = segmentedPill<TimeChoice>({
      label: 'Velocidad',
      value: '1',
      options: [
        { id: 'pause', label: 'Pausa', icon: ICONS.pause },
        { id: '1', label: '×1' },
        { id: '2', label: '×2' },
        { id: '4', label: '×4' },
      ],
      onChange: handlers.onTime,
    });
    this.time.el.hidden = true;
    this.camera = segmentedPill<CameraMode>({
      label: 'Vista de cámara',
      tone: 'white',
      value: mode,
      options: [
        { id: 'tilted', label: '3D' },
        { id: 'top', label: 'Cenital' },
      ],
      onChange: handlers.onCamera,
    });
    this.meter = frameButton({ label: 'Medidor de rendimiento', icon: ICONS.gaugeBold, pressed: meterOpen, onClick: handlers.onMeterToggle });
    this.el = h('div', { class: 'hud' }, h('div', { class: 'hud__top-right' }, this.time.el, this.paused, this.camera.el, this.meter.el));
  }

  /** Shows the time controls (inside a tile) with the clock's state, or hides them (on the map). */
  showTime(visible: boolean, choice: TimeChoice): void {
    this.time.el.hidden = !visible;
    this.time.set(choice);
    this.paused.hidden = !visible || choice !== 'pause';
  }

  /** The column's panels, for the interior's camera to keep clear of. */
  get column(): HTMLElement {
    return this.el.firstElementChild as HTMLElement;
  }
}
